// ── Severity matches Excel Severity Matrix ────────────────────────────────
export type IncidentSeverity = 'CRITICAL' | 'MODERATE' | 'MINOR';

export type IncidentType =
  | 'UNCLASSIFIED'       // assigned on create; agent reclassifies via [INCIDENT_TYPE:X]
  | 'WORKPLACE_INJURY'
  | 'DATA_BREACH'
  | 'IT_OUTAGE'
  | 'FIRE_EVACUATION'
  | 'SECURITY_THREAT'
  | 'ENVIRONMENTAL'
  | 'OTHER';

export type IncidentStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | 'REOPENED' | 'MERGED';

export interface CreateIncidentDTO {
  // Optional on create — backend defaults to UNCLASSIFIED and the agent
  // emits [INCIDENT_TYPE:X] once it analyses the description + early chat.
  type?:             IncidentType;
  title:             string;
  description:       string;
  location:          string;
  occurredAt:        string;
  severity:          IncidentSeverity;
  peopleInvolved?:   string;
  immediateActions?: string;
  reporterName:      string;
  reporterEmail:     string;
  clientId:          number;
}

// Stored / returned incidents always have a type — UNCLASSIFIED if the
// agent hasn't yet run. Narrow the optional field from the DTO so callers
// don't need to null-check every single usage.
export interface Incident extends Omit<CreateIncidentDTO, 'type'> {
  type:              IncidentType;
  id:                string;
  status:            IncidentStatus;
  sitrep?:           string;
  bedrockSessionId?: string;
  createdAt:         string;
  updatedAt:         string;
}

// ── Structured SitRep types ───────────────────────────────────────────────
export interface SitrepFact {
  text:   string;
  source: string;
}

export interface SitrepAction {
  action: string;
  status: 'Complete' | 'In Progress' | 'Pending';
}

export type SeverityRating = 'MINOR' | 'MODERATE' | 'CRITICAL' | null;

export interface SeverityRatings {
  people_injury?:             SeverityRating;
  people_illness?:            SeverityRating;
  people_threat?:             SeverityRating;
  people_missing?:            SeverityRating;
  people_key_personnel?:      SeverityRating;
  environment_contamination?: SeverityRating;
  assets_infrastructure?:     SeverityRating;
  assets_technology?:         SeverityRating;
  reputation_media?:          SeverityRating;
  reputation_consumer?:       SeverityRating;
  reputation_relationships?:  SeverityRating;
  livelihood_disruption?:     SeverityRating;
  livelihood_legal?:          SeverityRating;
  livelihood_regulatory?:     SeverityRating;
  livelihood_government?:     SeverityRating;
  livelihood_financial?:      SeverityRating;
}

export interface Sitrep {
  incidentId?:      string;
  dateOfIncident?:  string;
  timeStarted?:     string;
  facts?:           SitrepFact[];
  assumptions?:     string[];
  issuesImpacts?:   string;
  actions?:         SitrepAction[];
  recommendations?: string[];
  evidence?:        string;
  severityRatings?: SeverityRatings;
  updatedAt?:       string;
}

// ── Options lists ─────────────────────────────────────────────────────────
// UNCLASSIFIED is a transient state (set on create, overwritten by the
// agent's [INCIDENT_TYPE:X] marker). It is NOT in this list because it
// shouldn't appear as a pickable option; the display-only label is
// handled by incidentTypeLabel() in IncidentChat / Dashboard.
export const INCIDENT_TYPE_OPTIONS: { value: IncidentType; label: string }[] = [
  { value: 'WORKPLACE_INJURY',  label: 'Workplace Injury' },
  { value: 'DATA_BREACH',       label: 'Data Breach' },
  { value: 'IT_OUTAGE',         label: 'IT Outage' },
  { value: 'FIRE_EVACUATION',   label: 'Fire / Evacuation' },
  { value: 'SECURITY_THREAT',   label: 'Security Threat' },
  { value: 'ENVIRONMENTAL',     label: 'Environmental Incident' },
  { value: 'OTHER',             label: 'Other' },
];

// Human-readable label for any incident type value — including UNCLASSIFIED.
export function incidentTypeLabel(type: string | undefined): string {
  if (type === 'UNCLASSIFIED' || !type) return 'Awaiting classification…';
  return INCIDENT_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

export const SEVERITY_OPTIONS: { value: IncidentSeverity; label: string; colour: string; desc: string }[] = [
  { value: 'CRITICAL', label: 'Critical', colour: '#dc2626', desc: 'Activates full CIMT — agent chat required' },
  { value: 'MODERATE', label: 'Moderate', colour: '#ea580c', desc: 'Your initial assessment' },
  { value: 'MINOR',    label: 'Minor',    colour: '#16a34a', desc: 'Your initial assessment' },
];
